import { getAuthInstance } from "../firebase";
import { signInAnonymously, onAuthStateChanged } from "firebase/auth";

export const getApiBaseUrl = () => {
  return process.env.REACT_APP_API_URL || "http://localhost:3001";
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * Get a proxied URL for external images to bypass CORS
 * Only proxies external URLs, leaves internal/blob URLs unchanged
 */
export const getProxiedImageUrl = (url) => {
  if (!url) return url;
  
  // Don't proxy blob URLs, data URLs, or already-proxied URLs
  if (url.startsWith('blob:') || 
      url.startsWith('data:') || 
      url.includes('/api/brandkit/proxy-image') ||
      url.includes('firebasestorage.googleapis.com') ||
      url.includes('storage.googleapis.com')) {
    return url;
  }
  
  // Only proxy external http/https URLs
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return `${API_BASE_URL}/api/brandkit/proxy-image?url=${encodeURIComponent(url)}`;
  }
  
  return url;
};

const auth = getAuthInstance();

export const getCurrentUser = () => {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe();
        resolve(user);
      },
      reject
    );
  });
};

export const getAuthToken = async () => {
  let user = await getCurrentUser();

  if (!user) {
    await signInAnonymously(auth);
    user = await getCurrentUser();
  }

  try {
    return await user.getIdToken();
  } catch (error) {
    console.error("Error getting auth token:", error);
    throw new Error("Failed to get authentication token");
  }
};

export const getActiveOrgId = () => {
  try {
    const { store } = require("../../../store/store");
    const reduxOrgId = store.getState()?.app?.activeOrganization?._id;
    if (reduxOrgId) return reduxOrgId;
  } catch {}
  try {
    return localStorage.getItem("activeOrgId") || null;
  } catch {}
  return null;
};

export const getAuthHeader = async () => {
  const token = await getAuthToken();
  const headers = { Authorization: `Bearer ${token}` };
  const orgId = getActiveOrgId();
  if (orgId) {
    headers["X-Organization-Id"] = orgId;
  }
  return headers;
};

export const apiCall = async (method, endpoint, data = null, timeoutMs = 15000, retries = 2) => {
  const apiEndpoint = endpoint.startsWith("/api/")
    ? endpoint
    : `/api${endpoint}`;
  const url = `${API_BASE_URL}${apiEndpoint}`;

  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const headers = await getAuthHeader();
      headers["Content-Type"] = "application/json";

      const options = {
        method,
        headers,
        mode: "cors",
        credentials:
          process.env.NODE_ENV === "development" ? "include" : "same-origin",
        body: data ? JSON.stringify(data) : undefined,
        signal: controller.signal,
      };

      const response = await fetch(url, options);

      if (!response.ok) {
        const errorText = await response.text();
        const err = new Error(`API call failed: ${response.statusText}`);
        err.status = response.status;
        err.responseText = errorText;
        // Retry on 5xx server errors
        if (response.status >= 500 && attempt < retries) {
          lastError = err;
          await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
          continue;
        }
        throw err;
      }

      return response.json();
    } catch (error) {
      if (error.name === "AbortError") {
        lastError = new Error("Request timed out. Please try again.");
        if (attempt < retries) {
          await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
          continue;
        }
        throw lastError;
      }
      // Retry on network errors
      if (!error.status && attempt < retries) {
        lastError = error;
        await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
        continue;
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }
  throw lastError;
};

export const get = (endpoint) => apiCall("GET", endpoint);
export const post = (endpoint, data) => apiCall("POST", endpoint, data);
export const del = (endpoint) => apiCall("DELETE", endpoint);
export const patch = (endpoint, data) => apiCall("PATCH", endpoint, data);
